// frontend/src/modules/reports/pages/financial/ProjectProfitabilityPage.tsx
//
// Phase 4 (Reports Frontend), Task 4. Per-project cards. Binding honesty
// rule (Phase-3 contract §8): when `labor_cost_complete === false`, labor
// cost and margin render "—" (NEVER a fabricated number) with the note
// "Some team members have no hourly rate set". `invoiced` amounts are
// rendered per their OWN currency, never summed together or with the
// operating-currency figures.

import * as React from 'react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { VISUAL } from '../../../../design-system/visual';
import { formatMoney } from '../../../../design-system/format';
import { reportsApi, downloadReportCsv } from '../../api';
import type { ProfitabilityReport, ProfitabilityRow } from '../../types';
import { useReportFetch } from './useReportFetch';

function fmtHours(n: number | null): string {
  return n === null ? '—' : `${n.toFixed(1)}h`;
}

function ProjectCard({ row }: { row: ProfitabilityRow }) {
  const honestLaborNote = !row.labor_cost_complete;
  return (
    <div className={[VISUAL.card, 'p-6 space-y-4'].join(' ')}>
      <div>
        <h3 className="font-sans font-semibold text-[16px] text-foreground">{row.project_name}</h3>
        <p className="text-[12px] text-muted-foreground mt-0.5">
          {fmtHours(row.actual_hours)} actual{row.budget_hours !== null ? ` / ${fmtHours(row.budget_hours)} budget` : ''}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 text-[13px]">
        <div>
          <div className="text-muted-foreground">Billable value</div>
          <div className="font-semibold text-foreground mt-0.5">{formatMoney(row.billable_value, row.currency)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Budget</div>
          <div className="font-semibold text-foreground mt-0.5">
            {row.budget_amount !== null ? formatMoney(row.budget_amount, row.currency) : '—'}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">Labor cost</div>
          <div className="font-semibold text-foreground mt-0.5">
            {row.labor_cost === null ? '—' : formatMoney(row.labor_cost, row.currency)}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">Margin</div>
          <div className="font-semibold text-foreground mt-0.5">
            {row.margin === null ? '—' : formatMoney(row.margin, row.currency)}
          </div>
        </div>
      </div>

      {honestLaborNote && (
        <p className="text-[12px] text-muted-foreground italic">Some team members have no hourly rate set</p>
      )}

      {row.invoiced.length > 0 && (
        <div className="pt-3 border-t border-border/50">
          <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Invoiced</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
            {row.invoiced.map((i) => (
              <span key={i.currency} className="font-medium text-foreground">{formatMoney(i.amount, i.currency)}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ProjectProfitabilityPage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);

  const fetcher = range ? () => reportsApi.projectProfitability({ from: range.from, to: range.to }) : null;
  const result = useReportFetch<ProfitabilityReport>(fetcher, [range?.from, range?.to]);
  const forbidden = result.status === 403;

  const report = result.data;
  const rows = report?.rows ?? [];

  return (
    <ReportShell
      eyebrow="Reports / Financial"
      title="Project profitability"
      description="Billable value, labor cost, and margin per project."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      csv={range ? {
        filename: `project-profitability-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'project-profitability',
          { from: range.from, to: range.to },
          `project-profitability-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((row) => <ProjectCard key={row.project_id} row={row} />)}
          </div>
          <p className="text-[12px] text-muted-foreground">
            Margin = billable value − labor cost. Project expenses aren't linked to projects yet.
          </p>
        </div>
      )}
    </ReportShell>
  );
}
